import React, { useEffect, useRef, useState } from 'react';
import { EyeIcon } from '@heroicons/react/outline';
import Button from '../../../components/Button';
import ModalDialog from '../../../components/ModalDialog';
import { ColumnHeaderType } from '../../../types/tables';
import { DEFAULT_VISIBLE_ACTIONS } from '../helpers';

export type ActionSubOption = { field: string; headerName: string };

interface ColumnVisibilitySettingsProps {
  availableColumns: ColumnHeaderType[];
  visibleColumns: string[];
  onColumnVisibilityChange: (visibleColumns: string[]) => void;
  visibleActions?: string[];
  onVisibleActionsChange?: (visibleActions: string[]) => void;
  actionsSubOptions?: ActionSubOption[];
  hideTriggerButton?: boolean;
  exposeOpen?: (open: () => void) => void;
}

const ACTIONS_COLUMN_FIELD = 'sendFollowUp';

const COLUMN_DESCRIPTIONS: Record<string, string> = {
  name: "Show client's full name and profile picture",
  careTeam: 'Show practitioners assigned to clients',
  tags: 'View which tags are applied to clients',
  programStartDate:
    'Date that the client started their program (session, retreat, etc.)',
  programEndDate: 'Date that the program has ended (session, retreat, etc.)',
  dateAdded: 'Show the date that the client was added to Homecoming',
  unreadComments: 'See how many unread messages the client has sent you',
  sendFollowUp: 'View quick actions to chat, send Mail, or add private notes',
  inviteSent: 'When the last invite was sent',
  sendInvite: 'Button to send an invite to the client',
  archivedAt: 'When the client was moved to archive',
  actions: 'Button to restore a client from archive',
};

const ColumnVisibilitySettings: React.FC<ColumnVisibilitySettingsProps> = ({
  availableColumns,
  visibleColumns,
  onColumnVisibilityChange,
  visibleActions = [],
  onVisibleActionsChange,
  actionsSubOptions = [],
  hideTriggerButton = false,
  exposeOpen,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [localVisible, setLocalVisible] = useState<string[]>(visibleColumns);
  const [localVisibleActions, setLocalVisibleActions] =
    useState<string[]>(visibleActions);
  useEffect(() => setLocalVisible(visibleColumns), [visibleColumns]);
  useEffect(() => setLocalVisibleActions(visibleActions), [visibleActions]);

  const exposedOnceRef = useRef(false);
  useEffect(() => {
    if (exposeOpen && !exposedOnceRef.current) {
      exposeOpen(() => setIsModalOpen(true));
      exposedOnceRef.current = true;
    }
  }, [exposeOpen]);

  const handleColumnToggle = (columnField: string) => {
    const isRemoving = localVisible.includes(columnField);

    setLocalVisible((prev) =>
      isRemoving
        ? prev.filter((f) => f !== columnField)
        : [...prev, columnField],
    );

    if (columnField === ACTIONS_COLUMN_FIELD && onVisibleActionsChange) {
      setLocalVisibleActions(isRemoving ? [] : DEFAULT_VISIBLE_ACTIONS);
    }
  };

  const handleActionToggle = (actionField: string) => {
    const nextVisibleActions = localVisibleActions.includes(actionField)
      ? localVisibleActions.filter((f) => f !== actionField)
      : [...localVisibleActions, actionField];

    setLocalVisibleActions(nextVisibleActions);

    // Sync Actions column: hide when last action unchecked, show when first action checked
    setLocalVisible((cols) => {
      const hasActions = nextVisibleActions.length > 0;
      const hasActionsColumn = cols.includes(ACTIONS_COLUMN_FIELD);
      if (hasActions && !hasActionsColumn) {
        return [...cols, ACTIONS_COLUMN_FIELD];
      }
      if (!hasActions && hasActionsColumn) {
        return cols.filter((f) => f !== ACTIONS_COLUMN_FIELD);
      }
      return cols;
    });
  };

  const apply = () => {
    onColumnVisibilityChange(localVisible);
    onVisibleActionsChange?.(localVisibleActions);
    setIsModalOpen(false);
  };

  const handleCancel = () => {
    setLocalVisible(visibleColumns);
    setLocalVisibleActions(visibleActions);
    setIsModalOpen(false);
  };
  const hasActionsColumn =
    actionsSubOptions.length > 0 && onVisibleActionsChange;

  return (
    <>
      {!hideTriggerButton && (
        <Button
          title="Column visibility"
          IconComponent={EyeIcon}
          iconPosition="left"
          theme="secondary"
          size="small"
          onClick={() => setIsModalOpen(true)}
        />
      )}

      <ModalDialog
        isOpen={isModalOpen}
        setClosed={handleCancel}
        width="medium"
        overflow="auto"
        fullHeight
        title="Column visibility"
        leftFooter={
          <Button
            title="Cancel"
            theme="secondary"
            size="small"
            onClick={handleCancel}
          />
        }
        primaryActionTitle="Apply"
        primaryActionOnClick={apply}
        bodyClassName="flex flex-col min-h-0 pt-4"
      >
        <div className="flex min-h-0 flex-1 flex-col pr-1">
          <div className="mb-4 flex items-center gap-3">
            <EyeIcon className="h-6 w-6 text-secondary-100" />
            <span className="text-caption font-medium text-green-150">
              Column information
            </span>
          </div>
          {availableColumns.length === 0 ? (
            <div className="py-5 text-small-caption text-neutral-110">
              No columns
            </div>
          ) : (
            <div>
              {availableColumns.map((column) => {
                const isActionsColumn =
                  column.field === ACTIONS_COLUMN_FIELD && hasActionsColumn;
                const selected = localVisible.includes(column.field);
                const columnId = `column-${column.field}`;
                const description =
                  COLUMN_DESCRIPTIONS[column.field] ?? column.headerName;

                return (
                  <div key={column.field} className="flex flex-row py-5">
                    <div className="flex flex-1 items-start gap-3">
                      <input
                        id={columnId}
                        type="checkbox"
                        checked={selected}
                        onChange={() => handleColumnToggle(column.field)}
                        className="mt-0.5 h-5 w-5 shrink-0 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-secondary-125"
                      />
                      <div className="flex-1">
                        <label
                          htmlFor={columnId}
                          className="cursor-pointer text-caption font-medium text-green-150"
                        >
                          {column.headerName}
                        </label>
                        <div className="text-small-caption text-neutral-125">
                          {description}
                        </div>
                        {isActionsColumn && (
                          <div className="mt-5 space-y-2 pl-4">
                            {actionsSubOptions.map((action) => {
                              const actionSelected =
                                localVisibleActions.includes(action.field);
                              const actionId = `action-${action.field}`;
                              return (
                                <div
                                  key={action.field}
                                  className="flex items-center gap-2"
                                >
                                  <input
                                    id={actionId}
                                    type="checkbox"
                                    checked={actionSelected}
                                    onChange={() =>
                                      handleActionToggle(action.field)
                                    }
                                    className="h-5 w-5 shrink-0 cursor-pointer rounded border-neutral-100 text-secondary-125 focus:ring-secondary-125"
                                  />
                                  <label
                                    htmlFor={actionId}
                                    className="cursor-pointer text-caption text-neutral-125"
                                  >
                                    {action.headerName}
                                  </label>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </ModalDialog>
    </>
  );
};

export default ColumnVisibilitySettings;
